import { useCallback, useEffect, useRef, useState } from "react";
import PaymentService from "../services/paymentService";

const FINAL_STATUSES = ["Processed", "Error", "Cancelled", "Expired", "Timeout"];

const useKaspiPayment = ({ orderNumber, autoRedirect = false, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [kaspiData, setKaspiData] = useState(null);
  const [status, setStatus] = useState(null);
  const [amount, setAmount] = useState(undefined);
  const [statusStartedAt, setStatusStartedAt] = useState(null);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const redirectTimeoutRef = useRef(null);
  const isRedirectingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    clearTimers();
    setLoading(false);
    setKaspiData(null);
    setStatus(null);
    setAmount(undefined);
    setStatusStartedAt(null);
    setError(null);
    isRedirectingRef.current = false;
  }, [clearTimers, stopPolling]);

  const scheduleReset = useCallback(
    (delay = 5000) => {
      clearTimers();
      timeoutRef.current = setTimeout(() => {
        reset();
      }, delay);
    },
    [clearTimers, reset],
  );

  const startPayment = useCallback(async () => {
    if (!orderNumber || loading || kaspiData) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await PaymentService.createKaspiPayment(orderNumber);

      setKaspiData(data);
      setAmount(data.amount);
      setStatus("QrTokenCreated");
      setStatusStartedAt(Date.now());

      window.open(data.paymentLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Kaspi create payment error:", e);
      setError("Не удалось создать Kaspi-платеж");
      setStatus("Error");
      setStatusStartedAt(Date.now());
    } finally {
      setLoading(false);
    }
  }, [kaspiData, loading, orderNumber]);

  useEffect(() => {
    if (!kaspiData?.paymentId) {
      return;
    }

    stopPolling();

    const pollingInterval = Math.max(
      Number(kaspiData.pollingInterval || 2) * 1000,
      1000,
    );

    intervalRef.current = setInterval(async () => {
      try {
        const result = await PaymentService.getKaspiStatus(kaspiData.paymentId);
        const nextStatus = result.kaspiStatus;

        setStatus((prevStatus) => {
          if (prevStatus !== nextStatus) {
            setStatusStartedAt(Date.now());
          }

          return nextStatus;
        });

        if (nextStatus === "Processed" && !isRedirectingRef.current) {
          stopPolling();
          isRedirectingRef.current = true;

          if (onSuccess) {
            await onSuccess();
          }

          if (autoRedirect) {
            redirectTimeoutRef.current = setTimeout(() => {
              window.location.assign(
                `/result?order=${orderNumber}&status=success`,
              );
            }, 2000);
          }
        }

        if (nextStatus === "Error") {
          stopPolling();
          setStatus((prevStatus) => {
            if (prevStatus === "Wait") {
              return "Cancelled";
            }

            return "Error";
          });
          scheduleReset(4000);

          if (autoRedirect) {
            redirectTimeoutRef.current = setTimeout(() => {
              window.location.assign(`/result?order=${orderNumber}&status=fail`);
            }, 2000);
          }
        }
      } catch (e) {
        console.error("Kaspi status polling error:", e);
      }
    }, pollingInterval);

    return () => stopPolling();
  }, [
    autoRedirect,
    kaspiData,
    onSuccess,
    orderNumber,
    scheduleReset,
    stopPolling,
  ]);

  useEffect(() => {
    if (!status || !kaspiData || !statusStartedAt) {
      return;
    }

    if (FINAL_STATUSES.includes(status)) {
      return;
    }

    const timer = setInterval(() => {
      const now = Date.now();

      if (status === "QrTokenCreated") {
        const timeout = Number(kaspiData.linkActivationTimeout || 60) * 1000;

        if (now - statusStartedAt > timeout) {
          setStatus("Expired");
          stopPolling();
          scheduleReset(5000);
        }
      }

      if (status === "Wait") {
        const timeout =
          Number(kaspiData.paymentConfirmationTimeout || 120) * 1000;

        if (now - statusStartedAt > timeout) {
          setStatus("Timeout");
          stopPolling();
          scheduleReset(5000);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [kaspiData, scheduleReset, status, statusStartedAt, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      clearTimers();
    };
  }, [clearTimers, stopPolling]);

  return {
    loading,
    error,
    kaspiData,
    status,
    amount,
    isActive: Boolean(kaspiData),
    startPayment,
    reset,
  };
};

export default useKaspiPayment;
